import { FC } from 'react';
import { Box, IconButton, Modal, ModalProps, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import { theme } from '@store';

type ModalSize = 'medium' | 'large' | 'xl';

interface CustomModalProps extends Omit<ModalProps, 'open' | 'children'> {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  isLoading?: boolean;
  size?: ModalSize;
  hideHeader?: boolean;
  hideCloseIcon?: boolean;
  disableCloseOnOutsideClick?: boolean;
  subTitle?: React.ReactNode;
  disableOverflow?: boolean;
  height?: number;
}

const modalWidth: Record<ModalSize, string> = {
  medium: '400px',
  large: '600px',
  xl: '900px',
};

export const CustomModal: FC<CustomModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  isLoading = false,
  hideHeader = false,
  hideCloseIcon = false,
  disableCloseOnOutsideClick = false,
  size = 'large',
  disableOverflow = false,
  subTitle,
  height,
  ...props
}) => {
  return (
    <Modal
      open={isOpen}
      onClose={(event, reason) => {
        if (disableCloseOnOutsideClick) {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }

        onClose();
      }}
      sx={{ display: 'grid', placeItems: 'center' }}
      {...props}
    >
      <Box
        sx={{
          padding: '0 24px',
          width: modalWidth[size],
          backgroundColor: theme.palette.common.white,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          maxHeight: height ? height : 'calc(100vh - 150px)',
          overflow: disableOverflow ? 'unset' : 'auto',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '10px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&:focus': {
            outline: 'none',
          },
          outline: 'none',
        }}
      >
        {!hideHeader && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              pt: 3,
              pb: 2,
              mb: 1,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Box>
              <Typography variant='h6'>{title}</Typography>
              {subTitle && (
                <Typography
                  variant='caption'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.disabled',
                  }}
                >
                  {subTitle}
                </Typography>
              )}
            </Box>

            {!hideCloseIcon && (
              <IconButton
                sx={{
                  width: '30px',
                  height: '30px',
                  display: 'grid',
                  padding: 0,
                  placeItems: 'center',
                  ':hover': {
                    cursor: 'pointer',
                    bgcolor: theme => theme.palette.grey[50],
                    borderRadius: '6px',
                  },
                }}
                disabled={isLoading}
                onClick={onClose}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        )}
        {children}
      </Box>
    </Modal>
  );
};
